import { cleanStegaForTextTransform } from "~/features/sanity/stega";
export const __IS_CLIENT__ = typeof window !== "undefined";
export const __IS_SERVER__ = typeof window === "undefined";
export const __IS_ANALYZING__ = process.env.ANALYZE === "true";
export const __IS_DEV__ = process.env.NODE_ENV === "development";
export const __IS_PROD__ = process.env.NODE_ENV === "production";

/**
 * Make a single property required.
 */
export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

/**
 * Exclude all properties of `U` from `T`.
 */
export type ExcludeMembers<T, U> = Pick<T, Exclude<keyof T, keyof U>>;

/**
 * Remove null or undefined values from an array.
 *
 * @param value The optional value
 * @returns Whether the value is not null or undefined
 */
export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

/**
 * Check if a value exits and is not an empty array, object or string.
 * Checks array and object recursively up to a depth of 4. Anything
 * nested deeper than that is considered to be an item.
 *
 * @param value The value to check.
 * @returns Whether the value exits and has entries.
 */
export function hasItems<T>(value: T, depth = 0): value is NonNullable<T> {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return false;
    }
    if (depth >= 4) {
      return true;
    }
    for (let i = 0; i < value.length; i++) {
      if (hasItems(value[i], depth + 1)) {
        return true;
      }
    }
    return false;
  }
  if (typeof value === "object") {
    for (const key in value) {
      if (hasItems(value[key], depth + 1)) {
        return true;
      }
    }
    return false;
  }
  return true;
}

/**
 * Get the first or last item from a list of optional items.
 *
 * @param source The item or list of items.
 * @returns The item if any.
 */
function take<Item>(edge: "first" | "last", source?: Item[] | Item) {
  if (!source) {
    return;
  }
  if (Array.isArray(source)) {
    const i = edge === "first" ? 0 : source.length - 1;
    return source[i];
  }
  return source;
}

/**
 * Get the first item from a list of optional items.
 *
 * @param source The item or list of items.
 * @returns The first item if any.
 */
export function takeFirst<Item>(source?: Item[] | Item) {
  return take("first", source);
}

/**
 * Get the last item from a list of optional items.
 *
 * @param source The item or list of items.
 * @returns The last item if any.
 */
export function takeLast<Item>(source?: Item[] | Item) {
  return take("last", source);
}

/**
 * Get a random item from a list of optional items.
 *
 * @param source The list of items.
 * @returns A random item from the list.
 */
export function takeRandom<Item>(source?: Item[]) {
  if (!source) {
    return;
  }
  return source[Math.floor(Math.random() * source.length)];
}
export function debounce<F extends (...args: any[]) => any>(func: F, wait = 150): F {
  let timeoutID: ReturnType<typeof setTimeout> | undefined;
  return ((...args: Parameters<F>) => {
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
    timeoutID = setTimeout(() => func(...args), wait);
  }) as F;
}
export function throttle(fn: Function, wait: number = 150) {
  let inThrottle: boolean, lastFn: ReturnType<typeof setTimeout>, lastTime: number;
  return function (this: any) {
    const context = this,
      args = arguments;
    if (!inThrottle) {
      fn.apply(context, args);
      lastTime = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFn);
      lastFn = setTimeout(() => {
        if (Date.now() - lastTime >= wait) {
          fn.apply(context, args);
          lastTime = Date.now();
        }
      }, Math.max(wait - (Date.now() - lastTime), 0));
    }
  };
}
export function padNumber(n: number): string {
  return n < 10 ? "0" + n : n.toString();
}
export function round(num: number, decimals = 2) {
  return parseFloat(num.toFixed(decimals));
}
export function scrollElementIntoView(el: React.RefObject<HTMLElement | null> | string, padding = 0) {
  let element: HTMLElement | null;
  if (typeof el === "string") {
    element = document.getElementById(el);
  } else {
    element = el.current;
  }
  if (element) {
    const rect = element.getBoundingClientRect();
    const top = rect.top + window.pageYOffset - padding;
    window.scroll({
      top,
      behavior: "smooth"
    });
  }
}
export function stripHtmlTags(htmlString: string) {
  // Strip stega first: this collapses rich text into a single derived plain-text line (concatenating
  // blocks, often clamped), which would otherwise leave stray/partial stega payloads for the visual
  // editor scanner. The canonical field stays click-to-edit via its primary rich-text render path.
  const decodedString = cleanStegaForTextTransform(htmlString).replace(/&amp;/g, "&").replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec));
  return decodedString.replace(/(<([^>]+)>)/gi, "");
}

// Removes query params from a path
export function clearRouterPath(path: string) {
  return path.replace(/\?.*$/, "");
}
export function mapRange(value: number, source: [number, number], target: [number, number]): number {
  return (value - source[0]) / (source[1] - source[0]) * (target[1] - target[0]) + target[0];
}
export function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}